import { useCallback, useMemo, useRef } from 'react';

import {
  useRegisterWidgetSitePanelSave,
  useWidgetSitePanelCtx,
  useWidgetSiteTabbedShellCtx,
} from '@/components/widgetConfigSiteTabbedShell/index.ts';
import { useLayoutProps } from '@/hooks';

import { getSiteScopedChartConfig } from '../../chartHelperFunctions/getSiteScopedChartConfig.ts';
import {
  AggregatedChartConfigType,
  BoxPlotChartConfigType,
  ChartConfigHandle,
  ChartConfigType,
  DialogMode,
  HistogramChartConfigType,
  LinearChartConfigType,
  PlotType,
  ScatterPlotChartConfigType,
} from '../../types.ts';
import { AggregatedChartConfig } from './AggregatedChartConfig.tsx';
import { BoxPlotChartConfig } from './BoxPlotChartConfig.tsx';
import { HistogramChartConfig } from './HistogramChartConfig.tsx';
import { LinearChartConfig } from './LinearChartConfig.tsx';
import { ScatterPlotChartConfig } from './ScatterPlotChartConfig.tsx';

type ChartConfigBodyProps = {
  dialogMode: DialogMode;
  rootId?: string;
};

export const ChartConfigBody = ({ dialogMode, rootId }: ChartConfigBodyProps) => {
  const { onSave, section } = useWidgetSitePanelCtx();
  const { defaultSiteKey, shellWidgetId } = useWidgetSiteTabbedShellCtx();
  const { config, update } = useLayoutProps<ChartConfigType | undefined>({
    widgetId: shellWidgetId,
    defaultConfig: undefined,
  });
  const siteScopedConfig = useMemo(
    () => getSiteScopedChartConfig(config, section.siteKey, defaultSiteKey),
    [config, defaultSiteKey, section.siteKey]
  );
  const configRef = useRef<ChartConfigHandle>(null);

  const save = async () => {
    await configRef.current?.save();
  };

  useRegisterWidgetSitePanelSave(save);

  const persistAndSave = useCallback(
    (plotType: PlotType) => (configWidgetId: string, chartConfig: ChartConfigType) => {
      update({ widgetId: configWidgetId, config: chartConfig });
      onSave({ ...chartConfig, plotType });
    },
    [onSave, update]
  );

  const sharedProps = { id: shellWidgetId, ref: configRef, rootId };

  switch (dialogMode) {
    case PlotType.BOXPLOT:
      return (
        <BoxPlotChartConfig
          {...sharedProps}
          onSave={persistAndSave(PlotType.BOXPLOT)}
          config={siteScopedConfig as BoxPlotChartConfigType | undefined}
        />
      );
    case PlotType.LINEAR:
      return (
        <LinearChartConfig
          {...sharedProps}
          onSave={persistAndSave(PlotType.LINEAR)}
          config={siteScopedConfig as LinearChartConfigType | undefined}
        />
      );
    case PlotType.SCATTER:
      return (
        <ScatterPlotChartConfig
          {...sharedProps}
          onSave={persistAndSave(PlotType.SCATTER)}
          config={siteScopedConfig as ScatterPlotChartConfigType | undefined}
        />
      );
    case PlotType.HISTOGRAM:
      return (
        <HistogramChartConfig
          {...sharedProps}
          onSave={persistAndSave(PlotType.HISTOGRAM)}
          config={siteScopedConfig as HistogramChartConfigType | undefined}
        />
      );
    case PlotType.AGGREGATED:
      return (
        <AggregatedChartConfig
          {...sharedProps}
          onSave={persistAndSave(PlotType.AGGREGATED)}
          config={siteScopedConfig as AggregatedChartConfigType | undefined}
        />
      );
    default:
      return null;
  }
};
